import { App } from '@capacitor/app';
import { filter } from 'rxjs/operators';
import {Component, OnInit} from '@angular/core';
import {SubscriptionLike as ISubscription} from 'rxjs';
import {NavigationEnd, Router} from '@angular/router';
import { Platform } from '@angular/cdk/platform';
import { SplashScreen } from '@capacitor/splash-screen';
import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import { SafeArea } from 'capacitor-plugin-safe-area';

declare const window: any;
declare const gtag: Function;

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.css']
})
export class AppComponent implements OnInit {

  appLoader = false;
  subscription: ISubscription;
  title = 'app works!';
  constructor(private platform: Platform, private router: Router) {
    this.initApp();
    this.handleDeepLinks();
  }
  ngOnInit() {
    this.setUpAnalytics();
  }

  async initApp() {
      if (this.platform.ANDROID) {
        App.addListener('backButton', ({ canGoBack }) => {
          if (!canGoBack) {
            this.showExitConfirmation();
          } else {
            window.history.back();
          }
        });
      }
      await this.applySafeAreaInsets();
      await this.configureStatusBar();
      await this.handleSplashScreen();
      //Splash Screen
    }

  /**
   * Publishes the top safe-area inset as the --safe-top CSS variable.
   *
   * Needed because targetSdk is now 36: from Android 15 (API 35) the platform
   * enforces edge-to-edge, the system bars turn transparent and are drawn over
   * the WebView, and setOverlaysWebView(false) no longer reserves the space.
   * Without a reserved inset the status bar sits on top of the app header again.
   *
   * env(safe-area-inset-top) alone is not enough here - Android WebView support
   * for it is inconsistent across versions, which is precisely why this project
   * already depends on capacitor-plugin-safe-area. The plugin queries the real
   * window insets natively, so it is correct on both platforms; the CSS keeps
   * env() as a fallback for plain browsers.
   */
  private async applySafeAreaInsets() {
    if (!Capacitor.isNativePlatform()) {
      return;
    }
    const setTop = (top: number) =>
      document.documentElement.style.setProperty('--safe-top', `${top}px`);
    try {
      const { insets } = await SafeArea.getSafeAreaInsets();
      setTop(insets.top);
      // Insets change on rotation and on foldables; keep the variable in sync.
      await SafeArea.addListener('safeAreaChanged', ({ insets: changed }) =>
        setTop(changed.top)
      );
    } catch (e) {
      console.warn('[SafeArea] inset detection failed', e);
    }
  }

  /**
   * Makes the status bar look the same on every platform and OS version.
   *
   * This has to be done at runtime: @capacitor/status-bar 6.0.2 reads nothing
   * from capacitor.config.ts, so the old config block was dead weight.
   *
   * Both platforms take the SAME path - the bar floats over the WebView and the
   * header paints the brand red strip behind it, sized by --safe-top (see
   * .compact-navbar::before). That uniformity is deliberate: with targetSdk 36,
   * Android 15+ enforces edge-to-edge and ignores both setOverlaysWebView(false)
   * and setBackgroundColor, so the pre-15 approach of letting the system draw a
   * red bar would look different on newer devices. Asking for the overlay
   * explicitly makes Android 14 behave the way Android 16 is forced to.
   *
   * setBackgroundColor is deliberately not called: the bar is transparent, and
   * on Android 15+ the API is a deprecated no-op anyway.
   *
   * Style.Dark means LIGHT icons - the enum names the background it suits, and
   * the strip behind them is red on both platforms.
   */
  private async configureStatusBar() {
    if (!Capacitor.isNativePlatform()) {
      return;
    }
    try {
      if (Capacitor.getPlatform() === 'android') {
        // Android-only API; call.unimplemented() in StatusBarPlugin.swift.
        await StatusBar.setOverlaysWebView({ overlay: true });
      }
      await StatusBar.setStyle({ style: Style.Dark });
    } catch (e) {
      // Never let status bar chrome block startup — the splash screen and the
      // rest of initApp() still need to run.
      console.warn('[StatusBar] configuration skipped', e);
    }
  }

  setUpAnalytics() {
        this.router.events.pipe(filter(event => event instanceof NavigationEnd))
            .subscribe((event: NavigationEnd) => {
                gtag('config', 'G-XTFQ1BWVN6',
                    {
                        page_path: event.urlAfterRedirects
                    }
                );
            });
    }

  showExitConfirmation() {
    const shouldExit = confirm('Do you really want to exit the app?');
    if (shouldExit) {
      App.exitApp();
    }
  }

  async handleSplashScreen() {
    await SplashScreen.show({
      showDuration: 3000,
      autoHide: true
    });

    setTimeout(async () => {
      await SplashScreen.hide();
    }, 3000);
  }

  handleDeepLinks() {
    App.addListener('appUrlOpen', (data) => {
      const path = data.url.split('smartdukaan.com/')[1];
      if (path) {
        this.router.navigate([`/${path}`]);
      }
    });
  }

}
